import Num from '@flipster/utils/num';
import type { OrderMarginType } from '@flipster/utils/types/base';

import type { DisplayedPnlType } from '../pnl-setting-modal/state';
import type { UpnlBasis } from '../upnl-basis/state';

type NumericLike = string | { toString(): string };

export interface DisplayPnlInput {
  unrealizedPnl: NumericLike;
  netPnl: NumericLike;
  unrealizedPnlMark?: NumericLike;
  unrealizedPnlLast?: NumericLike;
  unrealizedPnlBbo?: NumericLike;
  netPnlMark?: NumericLike;
  netPnlLast?: NumericLike;
  netPnlBbo?: NumericLike;
}

export interface DisplayPnlFields {
  unrealizedPnl: string;
  netPnl: string;
  unrealizedPnlMark?: string;
  unrealizedPnlLast?: string;
  unrealizedPnlBbo?: string;
  netPnlMark?: string;
  netPnlLast?: string;
  netPnlBbo?: string;
}

export interface DisplayPnlPercentInput extends DisplayPnlInput {
  marginType: OrderMarginType;
  initPositionMargin: NumericLike;
  isolatedMarginInitial: NumericLike;
  isolatedMarginAdded?: NumericLike;
}

export interface DisplayPnlPercentFields extends DisplayPnlFields {
  marginType: OrderMarginType;
  initPositionMargin: string;
  isolatedMarginInitial: string;
  isolatedMarginAdded?: string;
}

export interface GetDisplayPnlOptions {
  pnlMode: DisplayedPnlType;
  upnlBasis: UpnlBasis;
}

function normalizeDisplayPnlFields(position: DisplayPnlInput): DisplayPnlFields {
  return {
    unrealizedPnl: position.unrealizedPnl.toString(),
    netPnl: position.netPnl.toString(),
    unrealizedPnlMark: position.unrealizedPnlMark?.toString(),
    unrealizedPnlLast: position.unrealizedPnlLast?.toString(),
    unrealizedPnlBbo: position.unrealizedPnlBbo?.toString(),
    netPnlMark: position.netPnlMark?.toString(),
    netPnlLast: position.netPnlLast?.toString(),
    netPnlBbo: position.netPnlBbo?.toString(),
  };
}

function normalizeDisplayPnlPercentFields(position: DisplayPnlPercentInput): DisplayPnlPercentFields {
  return {
    ...normalizeDisplayPnlFields(position),
    marginType: position.marginType,
    initPositionMargin: position.initPositionMargin.toString(),
    isolatedMarginInitial: position.isolatedMarginInitial.toString(),
    isolatedMarginAdded: position.isolatedMarginAdded?.toString(),
  };
}

// ROLLBACK(last-price): MM/BE 준비 완료 시 재활성화 — basis 필드 선택 (missing 시 legacy fallback).
// 원본:
// /**
//  * Basis-specific WS fields (e.g. unrealizedPnlMark) may be absent on first paint.
//  * When missing, fall back to legacy unrealizedPnl/netPnl — BBO-aligned server defaults.
//  * The uPnL basis selector can still show Mark/Last while the displayed value uses this fallback.
//  */
// function getUnrealizedPnlByBasis(position: DisplayPnlFields, upnlBasis: UpnlBasis): string {
//   switch (upnlBasis) {
//     case 'mark':
//       return position.unrealizedPnlMark ?? position.unrealizedPnl;
//     case 'last':
//       return position.unrealizedPnlLast ?? position.unrealizedPnl;
//     case 'bbo':
//       return position.unrealizedPnlBbo ?? position.unrealizedPnl;
//     default:
//       upnlBasis satisfies never;
//       return position.unrealizedPnl;
//   }
// }
//
// function getNetPnlByBasis(position: DisplayPnlFields, upnlBasis: UpnlBasis): string {
//   switch (upnlBasis) {
//     case 'mark':
//       return position.netPnlMark ?? position.netPnl;
//     case 'last':
//       return position.netPnlLast ?? position.netPnl;
//     case 'bbo':
//       return position.netPnlBbo ?? position.netPnl;
//     default:
//       upnlBasis satisfies never;
//       return position.netPnl;
//   }
// }

/** Selects the P&L amount to display from `pnlMode` × `upnlBasis`. */
export function getDisplayPnl(position: DisplayPnlInput, { pnlMode, upnlBasis }: GetDisplayPnlOptions): string {
  return getDisplayPnlFromFields(normalizeDisplayPnlFields(position), { pnlMode, upnlBasis });
}

function getDisplayPnlFromFields(
  position: DisplayPnlFields,
  { pnlMode, upnlBasis: _upnlBasis }: GetDisplayPnlOptions,
): string {
  // ROLLBACK(last-price): BE가 unrealizedPnlBbo 등 basis 필드를 제거하므로
  // legacy unrealizedPnl/netPnl만 사용한다 (pre-99b7fe01과 동일).
  // 시그니처·call site는 유지해 재활성화 시 아래 원본만 복원하면 된다.
  // 원본: return pnlMode === 'netPnl' ? getNetPnlByBasis(position, _upnlBasis) : getUnrealizedPnlByBasis(position, _upnlBasis);
  return pnlMode === 'netPnl' ? position.netPnl : position.unrealizedPnl;
}

function getPositionMargin(position: DisplayPnlPercentFields) {
  switch (position.marginType) {
    case 'Cross':
      return Num(position.initPositionMargin);
    case 'Isolated':
      return Num(position.isolatedMarginInitial).plus(position.isolatedMarginAdded || '0');
    default:
      position.marginType satisfies never;
      return Num(0);
  }
}

/** ROI % for a single position using the selected `pnlMode` × `upnlBasis`. */
export function getDisplayPnlPercent(position: DisplayPnlPercentInput, options: GetDisplayPnlOptions): string {
  return getDisplayPnlPercentFromFields(normalizeDisplayPnlPercentFields(position), options);
}

function getDisplayPnlPercentFromFields(position: DisplayPnlPercentFields, options: GetDisplayPnlOptions): string {
  const margin = getPositionMargin(position);
  if (margin.isZero()) return '0';

  return Num(getDisplayPnlFromFields(position, options)).dividedBy(margin).multipliedBy(100).toString();
}

/** Sums display P&L across multiple positions (e.g. symbol-level PIP aggregate). */
export function aggregateDisplayPnl(positions: DisplayPnlInput[], options: GetDisplayPnlOptions): string {
  return positions.reduce((acc, position) => acc.plus(getDisplayPnl(position, options)), Num(0)).toString();
}

/** Portfolio-level ROI % = sum(display P&L) / sum(margin). */
export function aggregateDisplayPnlPercent(positions: DisplayPnlPercentInput[], options: GetDisplayPnlOptions): string {
  const { totalMargin, totalPnl } = positions.reduce(
    (acc, position) => {
      const normalized = normalizeDisplayPnlPercentFields(position);
      return {
        totalMargin: acc.totalMargin.plus(getPositionMargin(normalized)),
        totalPnl: acc.totalPnl.plus(getDisplayPnlFromFields(normalized, options)),
      };
    },
    { totalMargin: Num(0), totalPnl: Num(0) },
  );
  if (totalMargin.isZero()) return '0';

  return totalPnl.dividedBy(totalMargin).multipliedBy(100).toString();
}
